import isNumber from 'predicates/number';
import React, { useEffect } from 'react';
import { generatePath, useParams } from 'react-router-dom';

import LoaderOverlay from '../../general/LoaderOverlay';
import {
  CoursesService,
  FullSection,
  MediaService,
  UserAnswer,
  UserProgress
} from '../../general/ServerClient';
import { PageLevelComponent, parseStringAsInt } from '../../general/util';
import NotFoundPage from '../../NotFoundPage';
import { useGetRetryFetch } from '../page/Auth';
import { useAppContext, useBirthPlanCourse } from '../page/WebappContext';
import { WebappUrls } from '../page/WebappUrls';
import { SectionPage } from '../SectionPage/SectionPage';
import { load } from '../util/RemoteData';

/**
 * A page displaying a particular section in the Learn tab.
 */
export const PlanSectionPage: PageLevelComponent = () => {
  const { sectionId } = useParams<{ sectionId: string | undefined }>();
  const id = sectionId && parseStringAsInt(sectionId);
  const { courses, fullSection, sectionAnswers, birthPlanCourseProgress } =
    useAppContext();
  const retryFetchFullSection = useGetRetryFetch<FullSection>();
  const retryFetchSectionAnswers = useGetRetryFetch<UserAnswer[]>();
  const retryFetchUserProgress = useGetRetryFetch<UserProgress>();
  const birthPlanCourse = useBirthPlanCourse();
  useEffect(() => {
    if (isNumber(id)) {
      load(
        fullSection,
        () =>
          retryFetchFullSection(() =>
            CoursesService.coursesSectionsRetrieve(id)
          ),
        { sectionId: id }
      );
    }
  }, [id]);
  useEffect(() => {
    if (isNumber(id)) {
      load(
        sectionAnswers,
        () =>
          retryFetchSectionAnswers(() =>
            CoursesService.coursesSectionsUserAnswersList(id)
          ),
        { sectionId: id }
      );
    }
  }, [id]);
  const progress = isNumber(id)
    ? birthPlanCourseProgress.data?.sections[id]
    : undefined;
  if (fullSection.error || sectionAnswers.error) {
    if (fullSection.error instanceof Error) {
      if (fullSection.error.message.includes('Not Found')) {
        return <NotFoundPage />;
      }
    }
    console.error('Found error', fullSection.error);
    return <div>An unexpected error occurred while fetching this section.</div>;
  }
  return (
    <>
      {fullSection.data && sectionAnswers.data && progress && (
        <SectionPage
          {...{
            section: fullSection.data,
            userAnswers: sectionAnswers.data,
            progress,
            generateQuestionPath: ({ sectionId, questionId }) =>
              generatePath(WebappUrls.planQuestion, { sectionId, questionId }),
            onUserWatchedVideo: (videoId) => {
              MediaService.mediaVideoWatchedCreate(videoId).then(() => {
                if (birthPlanCourse) {
                  load(birthPlanCourseProgress, () =>
                    retryFetchUserProgress(() =>
                      CoursesService.coursesUserProgressRetrieve(
                        birthPlanCourse.id
                      )
                    )
                  );
                }
              });
            }
          }}
          back={{
            label: 'Back To All Birth Plans',
            destination: WebappUrls.birthPlan
          }}
          isBirthClass={false}
        />
      )}
      {(fullSection.loading ||
        sectionAnswers.loading ||
        courses.loading ||
        birthPlanCourseProgress.loading) && <LoaderOverlay />}
    </>
  );
};
